import { NodeSelection, TextSelection } from "@tiptap/pm/state"

import type { EditorState, Transaction } from "@tiptap/pm/state"

// A caret sitting next to an image deletes it outright, so select it first and
// let the second keystroke be the one that removes it.
export const selectAdjacentImage = (
	state: EditorState,
	direction: -1 | 1,
	nodeName = "image",
): Transaction | null => {
	const { selection } = state
	if (!(selection instanceof TextSelection) || !selection.empty) return null

	const $cursor = selection.$from
	if ($cursor.depth === 0) return null

	const atEdge =
		direction === -1
			? $cursor.parentOffset === 0
			: $cursor.parentOffset === $cursor.parent.content.size
	if (!atEdge) return null

	const $boundary = state.doc.resolve(
		direction === -1
			? $cursor.before($cursor.depth)
			: $cursor.after($cursor.depth),
	)
	const adjacent = direction === -1 ? $boundary.nodeBefore : $boundary.nodeAfter
	if (adjacent?.type.name !== nodeName) return null
	if (!NodeSelection.isSelectable(adjacent)) return null

	const imagePos =
		direction === -1 ? $boundary.pos - adjacent.nodeSize : $boundary.pos

	if ($cursor.parent.content.size === 0) {
		if (direction !== -1) return null
		return removeEmptyLineBelowImage(state, $cursor.depth, imagePos)
	}

	return state.tr
		.setSelection(NodeSelection.create(state.doc, imagePos))
		.scrollIntoView()
}

// The image margin hides that the empty line is gone, so the next Backspace
// used to take the image. Land on the following line; select the image last.
const removeEmptyLineBelowImage = (
	state: EditorState,
	depth: number,
	imagePos: number,
): Transaction => {
	const from = state.selection.$from.before(depth)
	const to = state.selection.$from.after(depth)
	const tr = state.tr.delete(from, to)
	const next = tr.doc.resolve(from).nodeAfter
	tr.setSelection(
		next?.isTextblock
			? TextSelection.create(tr.doc, from + 1)
			: NodeSelection.create(tr.doc, imagePos),
	)
	return tr.scrollIntoView()
}
